/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { delay } from "lodash";

import {
  CheckboxVisibility,
  CommandBar,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  ICommandBarItemProps,
  IDetailsRowProps,
  Stack,
  StackItem,
  Text,
} from "@fluentui/react";

import {
  detailsListStyles,
  headerStackStyles,
  headerTextStyles,
  ContainerWrapper,
} from "Components/Common/SharedTableStyles.styles";
import ConfirmationPopup from "Components/ConfirmationPopup/ConfirmationPopup";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { OperationStatus } from "Constants/OperationStatus";
import { defaultDelay } from "Constants/ProfileConstants";
import { IDistributionList } from "Interfaces/UserProfile/DistributionLists/IDistributionLists";

import { CommandBarWrapper, TableWrapper } from "./UserProfileDistributionList.styles";
import { IUserProfileDistributionListProps } from "./UserProfileDistributionListProps";

/**
 * The User Profile Distribution Lists component
 * @method
 * @returns {ReactElement} The User Profile Distribution Lists component
 */
const UserProfileDistributionList = (props: IUserProfileDistributionListProps): ReactElement => {
  const {
    distributionLists,
    allowEditing,
    dlRemoveStatus,
    dlRemoveError,
    dlRemoveAllStatus,
    dlRemoveAllError,
    onRemoveDl,
    onRemoveAllDl,
  } = props;

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [selectedDl, setSelectedDl] = useState<IDistributionList>();

  const columns = [
    {
      key: "dlDisplayName",
      name: "DL Name",
      fieldName: "dlDisplayName",
      minWidth: 100,
      maxWidth: 400,
      isResizable: true,
    },
    {
      key: "dlMail",
      name: "DL Email",
      fieldName: "dlMail",
      minWidth: 100,
      maxWidth: 400,
      isResizable: true,
    },
    {
      key: "joinedEmail",
      name: "User Email",
      fieldName: "joinedEmail",
      minWidth: 100,
      maxWidth: 400,
      isResizable: true,
    },
  ];

  const openConfirmationModal = (): void => {
    setIsConfirmationOpen(true);
  };

  const dismissConfirmationModal = (): void => {
    setIsConfirmationOpen(false);
  };

  const onClickRemove = (distributionList: IDistributionList): void => {
    if (!allowEditing) return;

    setIsPanelOpen(true);
    setSelectedDl(distributionList);
  };

  const dismissPanel = (): void => {
    setIsPanelOpen(false);
  };

  const renderItemColumn = (item: IDistributionList, _?: number, column?: IColumn): JSX.Element => {
    const fieldContent = item[column?.fieldName as keyof IDistributionList] as string;
    return <span>{fieldContent}</span>;
  };

  const onRenderRow = (props?: IDetailsRowProps, defaultRender?: any): React.ReactElement => {
    const distributionList = props!.item as IDistributionList;

    return defaultRender!({
      ...props,
      item: distributionList,
      onClick: () => onClickRemove(distributionList),
    });
  };

  const isRemovingDl = dlRemoveStatus === OperationStatus.Pending;
  const isRemovingAllDl = dlRemoveAllStatus === OperationStatus.Pending;

  const items: ICommandBarItemProps[] = [
    {
      key: "removeUserFromDl",
      text: "Remove all",
      iconProps: { iconName: "UserRemove" },
      disabled: distributionLists.length === 0,
      onClick: (): void => {
        openConfirmationModal();
      },
    },
  ];

  useEffect(() => {
    if (
      dlRemoveStatus === OperationStatus.Succeeded ||
      dlRemoveAllStatus === OperationStatus.Succeeded
    ) {
      delay(dismissPanel, defaultDelay);
    }
  }, [
    dlRemoveStatus === OperationStatus.Succeeded,
    dlRemoveAllStatus === OperationStatus.Succeeded,
  ]);

  return (
    <>
      <ContainerWrapper>
        <Stack>
          <Stack styles={headerStackStyles}>
            <Text styles={headerTextStyles}>DL membership</Text>
          </Stack>
          {allowEditing && (
            <CommandBarWrapper>
              <CommandBar items={items} ariaLabel="Remove all" />
            </CommandBarWrapper>
          )}
          <TableWrapper commandBarButtonPresent={allowEditing}>
            <DetailsList
              items={distributionLists}
              columns={columns}
              setKey="set"
              layoutMode={DetailsListLayoutMode.justified}
              ariaLabelForSelectionColumn="Toggle selection"
              ariaLabelForSelectAllCheckbox="Toggle selection for all items"
              checkButtonAriaLabel="select row"
              checkboxVisibility={CheckboxVisibility.hidden}
              onRenderRow={onRenderRow}
              onRenderItemColumn={renderItemColumn}
              styles={detailsListStyles}
            />
            {distributionLists.length < 1 && <>No distribution lists for this user.</>}
          </TableWrapper>
        </Stack>
      </ContainerWrapper>

      {isConfirmationOpen && (
        <ConfirmationPopup
          title="Join Request"
          description="This action will remove the user from all DLs and the Exchange tenant, are you sure?"
          confirmButtonText="Proceed"
          disableActions={isRemovingAllDl}
          onDismiss={dismissConfirmationModal}
          onConfirm={onRemoveAllDl}
          onCancel={dismissConfirmationModal}
        />
      )}

      <SidePanel
        isOpen={isPanelOpen}
        title={selectedDl?.dlDisplayName ?? ""}
        saveButtonLabel="Remove"
        error={dlRemoveError}
        disableActions={isRemovingDl}
        onDismiss={dismissPanel}
        onSave={(): void => {
          onRemoveDl(selectedDl as IDistributionList);
        }}
      >
        <Stack tokens={{ childrenGap: "1rem" }}>
          <StackItem>You can remove the user from this DL by clicking Remove.</StackItem>
        </Stack>
      </SidePanel>
    </>
  );
};

export default UserProfileDistributionList;
